/**
 * Web port of mobile components/diary/today-score.ts — identical behaviour;
 * only the import paths and the DailySummary → DayScoreInput hand-off in
 * computeTodayScore differ (the web DailySummary types sodium/protein totals
 * as optional, so they're passed field by field; `pct` already treats a
 * missing value as 0).
 *
 * Day-level Liver Score ("budget status") for the home hero card and the
 * Progress day grid. Canonical definition: docs/LIVER_MODEL_SPEC.md §4a.
 *
 * The per-meal scorer can't be reused on a day's running totals: it treats
 * 100% of a daily budget as maximum burden, so a normal within-budget day
 * would read "high" by evening. Instead the day score maps the worst ceiling
 * axis (% of daily budget used) onto the standard risk bands:
 *   ≤100% of every ceiling → 0–30 (safe)
 *   100–120%               → 31–60 (moderate)
 *   >120%                  → 61–100 (high; 100 at ≥200%)
 * Post-process overrides mirror the meal rules: any alcohol → ≥90 (driver
 * alcohol); any energy drink (stored driver `caffeine`) → ≥45.
 *
 * One computation (`scoreDay`) backs every caller: today's hero reads the live
 * summary + entries, and a past day in the grid reads its dailySummaries doc
 * (`scoreStoredDay`), whose `dayScore` block this module also builds.
 */

import type { ScoreBreakdown } from '@/liver/score';
import { entryHasAlcohol } from '@/lib/alcohol';
import type {
  DailySummary,
  DailySummaryDoc,
  DiaryEntry,
  StoredDayScoreInputs,
} from '@/types';

export type DayScore = Pick<ScoreBreakdown, 'score' | 'riskLevel' | 'driver' | 'percentages'>;

type CeilingAxis = 'addedSugar' | 'saturatedFat' | 'calories' | 'sodium';

// Driver tie-break order (spec §4): addedSugar > saturatedFat > calories > sodium.
const AXIS_ORDER: CeilingAxis[] = ['addedSugar', 'saturatedFat', 'calories', 'sodium'];

const pct = (value: number | undefined, limit: number | undefined): number =>
  limit && limit > 0 ? Math.round(((value ?? 0) / limit) * 1000) / 10 : 0;

export function dayScoreFromPct(worstPct: number): number {
  if (worstPct <= 100) return Math.round(Math.max(0, worstPct) * 0.3);
  if (worstPct <= 120) return Math.round(31 + ((worstPct - 100) / 20) * 29);
  return Math.min(100, Math.round(61 + ((worstPct - 120) / 80) * 39));
}

const bandFor = (score: number): ScoreBreakdown['riskLevel'] =>
  score <= 30 ? 'safe' : score <= 60 ? 'moderate' : 'high';

/** Everything §4a needs for one day. Fiber/protein only feed display percentages. */
export interface DayScoreInput {
  totals: {
    calories: number;
    addedSugarsG: number;
    saturatedFatG: number;
    sodiumMg: number;
    fiberG?: number;
    proteinG?: number;
  };
  limits: {
    calories: number;
    addedSugarsG: number;
    saturatedFatG: number;
    sodiumMg: number;
    fiberG?: number;
    proteinG?: number;
  };
  foodItems: number;
  hasAlcohol: boolean;
  hasEnergyDrink: boolean;
}

/** The day score, or null when nothing scoreable was logged (drinks-only / empty day). */
export function scoreDay(input: DayScoreInput): DayScore | null {
  if (input.foodItems === 0 && !input.hasAlcohol) return null;

  const { totals, limits } = input;
  const percentages: ScoreBreakdown['percentages'] = {
    addedSugar: pct(totals.addedSugarsG, limits.addedSugarsG),
    saturatedFat: pct(totals.saturatedFatG, limits.saturatedFatG),
    calories: pct(totals.calories, limits.calories),
    sodium: pct(totals.sodiumMg, limits.sodiumMg),
    fiber: limits.fiberG ? pct(totals.fiberG, limits.fiberG) : null,
    protein: limits.proteinG ? pct(totals.proteinG, limits.proteinG) : null,
  };

  let driver: ScoreBreakdown['driver'] = AXIS_ORDER[0];
  for (const axis of AXIS_ORDER) {
    if (percentages[axis] > percentages[driver as CeilingAxis]) driver = axis;
  }
  let score = dayScoreFromPct(percentages[driver as CeilingAxis]);

  if (input.hasEnergyDrink && score < 45) {
    score = 45;
    driver = 'caffeine';
  }
  if (input.hasAlcohol && score < 90) {
    score = 90;
    driver = 'alcohol';
  }

  return { score, riskLevel: bandFor(score), driver, percentages };
}

/** The entry-derived inputs: what counts as food, and the two overrides. */
export function dayScoreFlags(
  entries: DiaryEntry[],
): Pick<DayScoreInput, 'foodItems' | 'hasAlcohol' | 'hasEnergyDrink'> {
  return {
    foodItems: entries.filter((e) => !e.isFluid || (e.nutrients?.calories ?? 0) > 0).length,
    hasAlcohol: entries.some(entryHasAlcohol),
    hasEnergyDrink: entries.some((e) => e.analysis?.scoreBreakdown?.driver === 'caffeine'),
  };
}

/** The §4a totals from a web DailySummary (mobile passes `summary.totals` whole). */
function summaryTotals(summary: DailySummary): DayScoreInput['totals'] {
  const t = summary.totals;
  return {
    calories: t.calories,
    addedSugarsG: t.addedSugarsG,
    saturatedFatG: t.saturatedFatG,
    sodiumMg: t.sodiumMg ?? 0,
    fiberG: t.fiberG,
    proteinG: t.proteinG,
  };
}

export function computeTodayScore(
  summary: DailySummary | null,
  entries: DiaryEntry[],
): DayScore | null {
  if (!summary) return null;
  return scoreDay({ totals: summaryTotals(summary), limits: summary.limits, ...dayScoreFlags(entries) });
}

/**
 * The `dayScore` block persisted on a dailySummaries doc (DiaryContext writer).
 * Every value is a plain number / boolean: Firestore rejects `undefined`, and
 * this rides on the same write as the totals.
 */
/**
 * The stored inputs to write for `date`. A past day keeps the ceilings it was
 * first saved with: the summary is re-saved whenever a day is opened, and
 * writing today's limits then would re-judge old days (e.g. after a condition
 * change). Omitting `limits` works because the write is a merge.
 */
export function dayScoreInputsForWrite(
  inputs: StoredDayScoreInputs,
  date: string,
  today: string,
): StoredDayScoreInputs | Omit<StoredDayScoreInputs, 'limits'> {
  if (date === today) return inputs;
  const { limits: _limits, ...rest } = inputs;
  return rest;
}

export function storedDayScoreInputs(
  summary: DailySummary,
  entries: DiaryEntry[],
): StoredDayScoreInputs {
  const n = (v: number | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    limits: {
      calories: n(summary.limits.calories),
      addedSugarsG: n(summary.limits.addedSugarsG),
      saturatedFatG: n(summary.limits.saturatedFatG),
      sodiumMg: n(summary.limits.sodiumMg),
    },
    ...dayScoreFlags(entries),
  };
}

/**
 * A past day's score from its dailySummaries doc.
 *
 * Totals always come from the doc's top-level rollup (both the phone and the
 * web app keep it current). The day's own ceilings and override flags come
 * from its `dayScore` block. A doc without one (written before the block
 * existed, or only ever edited on the web) falls back to `fallbackLimits` —
 * the user's CURRENT limits — with no alcohol / energy-drink override; it
 * gains the block the next time the day is opened in the diary.
 *
 * When the web app has merged newer totals over a phone write, the block's
 * flags can lag (they only change on a phone write). Food is still judged
 * whenever the fresher totals show calories.
 */
export function scoreStoredDay(
  doc: Pick<DailySummaryDoc, 'totalItems' | 'calories' | 'addedSugar' | 'saturatedFat' | 'sodium' | 'dayScore'>,
  fallbackLimits: DayScoreInput['limits'],
): DayScore | null {
  if (!(doc.totalItems > 0)) return null;
  const block = doc.dayScore;
  const calories = doc.calories || 0;
  const hasAlcohol = block?.hasAlcohol === true;
  const blockFood = typeof block?.foodItems === 'number' ? block.foodItems : 0;
  return scoreDay({
    totals: {
      calories,
      addedSugarsG: doc.addedSugar || 0,
      saturatedFatG: doc.saturatedFat || 0,
      sodiumMg: doc.sodium || 0,
    },
    limits: block?.limits ?? fallbackLimits,
    foodItems: calories > 0 ? Math.max(1, blockFood) : blockFood,
    hasAlcohol,
    hasEnergyDrink: block?.hasEnergyDrink === true,
  });
}
